import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import logoWhite from '../../assets/new-design/logo-white.png';
import logoLarge from '../../assets/new-design/logo-large.png';
import './AppHeader.scss';

export type HeaderVariant = 'dark' | 'parceiro' | 'checkout';

interface Props {
  variant: HeaderVariant;
  /** variant="checkout": sobrescreve o destino do botão "Voltar" (padrão: home). */
  onBack?: () => void;
}

const NAV_LINKS = [
  { label: 'Assine', path: '/' },
  { label: 'Mapa de parceiros', path: '/mapa-parceiros' },
  { label: 'Serviços adicionais', path: '/vitrine' },
];

export function AppHeader({ variant, onBack }: Props) {
  const navigate = useNavigate();
  const location = useLocation();
  const [isScrolled, setIsScrolled] = useState(() => window.scrollY > 20);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (menuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [menuOpen]);

  useEffect(() => {
    const isHomePage = location.pathname === '/';
    if (variant !== 'dark' || !isHomePage) {
      setIsScrolled(true);
      return;
    }
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [variant, location.pathname]);

  if (variant === 'checkout') {
    return (
      <header className="app-header app-header--checkout">
        <button className="app-header__back-btn" onClick={onBack ?? (() => navigate('/'))}>
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.4} strokeLinecap="round" strokeLinejoin="round">
            <path d="M15 18l-6-6 6-6" />
          </svg>
          Voltar
        </button>
        <div className="app-header__logo-center" onClick={() => navigate('/')}>
          <img src={logoWhite} alt="WashPass" className="app-header__logo" />
        </div>
        <div className="app-header__spacer" />
      </header>
    );
  }

  if (variant === 'parceiro') {
    return (
      <header className="app-header app-header--parceiro">
        <div className="app-header__left">
          <img
            src={logoLarge}
            alt="WashPass"
            className="app-header__logo-large"
            onClick={() => navigate('/')}
          />
          <nav className="app-header__nav">
            {NAV_LINKS.map((link) => (
              <button
                key={link.path}
                className="app-header__nav-btn app-header__nav-btn--dimmed"
                onClick={() => navigate(link.path)}
              >
                {link.label}
              </button>
            ))}
          </nav>
        </div>
        <div className="app-header__right">
          <a
            href="https://panel.wash.app.br/login"
            target="_blank"
            rel="noopener noreferrer"
            className="app-header__login-link"
          >
            Entrar na conta
          </a>
        </div>
      </header>
    );
  }

  return (
    <>
      <header className={`app-header app-header--dark${isScrolled ? '' : ' app-header--transparent'}`}>
        <div className="app-header__left">
          <img
            src={logoWhite}
            alt="WashPass"
            className="app-header__logo"
            onClick={() => navigate('/')}
            style={{ cursor: 'pointer' }}
          />
          <nav className="app-header__nav">
            {NAV_LINKS.map((link) => {
              const isActive = location.pathname === link.path;
              return (
                <button
                  key={link.path}
                  className={`app-header__nav-btn ${isActive ? 'app-header__nav-btn--active' : 'app-header__nav-btn--inactive'}`}
                  onClick={() => navigate(link.path)}
                >
                  {link.label}
                </button>
              );
            })}
          </nav>
        </div>
        <div className="app-header__right">
          <button
            className="app-header__partner-btn"
            onClick={() => navigate('/parceiros')}
          >
            Área do parceiro
          </button>
          <button
            className="app-header__hamburger"
            onClick={() => setMenuOpen(true)}
            aria-label="Abrir menu"
          >
            <span />
            <span />
            <span />
          </button>
        </div>
      </header>

      {menuOpen && (
        <div className="app-header__mobile-menu">
          <div className="app-header__mobile-menu-top">
            <img src={logoWhite} alt="WashPass" className="app-header__logo" />
            <button
              className="app-header__mobile-close"
              onClick={() => setMenuOpen(false)}
              aria-label="Fechar menu"
            >
              ✕
            </button>
          </div>
          <nav className="app-header__mobile-nav">
            {NAV_LINKS.map((link) => {
              const isActive = location.pathname === link.path;
              return (
                <button
                  key={link.path}
                  className={`app-header__mobile-nav-btn${isActive ? ' app-header__mobile-nav-btn--active' : ''}`}
                  onClick={() => navigate(link.path)}
                >
                  {link.label}
                </button>
              );
            })}
          </nav>
          <button
            className="app-header__mobile-partner-btn"
            onClick={() => navigate('/parceiros')}
          >
            Área do parceiro
          </button>
        </div>
      )}
    </>
  );
}
